import React, { useState } from 'react'

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import { alpha, Chip, Collapse, IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material'
import CustomerFeatureHistory from 'features/customers/components/features/CustomerFeatureHistory'
import CustomerFeatureSource from 'features/customers/components/features/CustomerFeatureSource'
import CustomerFeatureUsage from 'features/customers/components/features/CustomerFeatureUsage'
import CustomerFeatureValue, {
  FeatureValueText,
  formatFeatureValue,
  isUnlimitedFeatureValue,
} from 'features/customers/components/features/CustomerFeatureValue'
import { REMOVE_OVERRIDE } from 'features/customers/utils/featureEdits'
import type { FeatureRow } from 'features/customers/utils/featureRows'

type Props = {
  row: FeatureRow
  edited: unknown
  onChange: (name: string, value: unknown) => void
}

const CustomerFeatureRow = ({ row, edited, onChange }: Props) => {
  const [open, setOpen] = useState(false)

  const isEdited = edited !== undefined

  const canClear = row.editable && row.overridden && !isEdited

  const captionText = isEdited
    ? `was ${formatFeatureValue(row.value, row.name)}`
    : isUnlimitedFeatureValue(row.name, row.value)
      ? '= Unlimited'
      : undefined

  const caption = captionText ? (
    <Typography color="text.secondary" sx={{ display: 'block' }} variant="caption">
      {captionText}
    </Typography>
  ) : undefined

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > td:first-of-type': row.overridden
            ? { boxShadow: theme => `inset 3px 0 0 ${theme.palette.primary.main}` }
            : {},
          ...(row.overridden
            ? {
                backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
                '&.MuiTableRow-hover:hover': {
                  backgroundColor: theme => alpha(theme.palette.primary.main, 0.09),
                },
              }
            : {}),
          ...(isEdited
            ? {
                backgroundColor: theme => alpha(theme.palette.warning.main, 0.14),
                '&.MuiTableRow-hover:hover': {
                  backgroundColor: theme => alpha(theme.palette.warning.main, 0.22),
                },
              }
            : {}),
        }}>
        <TableCell sx={{ pl: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Tooltip title={row.description ?? ''}>
              <Typography
                sx={{
                  fontWeight: row.overridden ? 500 : 400,
                  ...(row.description
                    ? {
                        cursor: 'help',
                        textDecoration: 'underline dotted',
                        textDecorationColor: theme => theme.palette.divider,
                        textUnderlineOffset: 3,
                      }
                    : {}),
                }}
                variant="body2">
                {row.label}
              </Typography>
            </Tooltip>
            {!row.editable && (
              <Tooltip title="This tab cannot change this one. The rule on the left says whether it is stored on the customer.">
                <Chip label="Read-only" size="small" sx={{ height: 20 }} variant="outlined" />
              </Tooltip>
            )}
            {isEdited && <Chip color="warning" label="Unsaved" size="small" sx={{ height: 20 }} />}
            {/* The rule and wash say "stored here"; only this says the value is actually different. */}
            {row.differs && !isEdited && (
              <Tooltip title={`The plan gives ${formatFeatureValue(row.planValue, row.name)}`}>
                <Chip color="info" label="Differs" size="small" sx={{ height: 20 }} variant="outlined" />
              </Tooltip>
            )}
          </Stack>
        </TableCell>

        <TableCell>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <CustomerFeatureValue edited={edited} onChange={onChange} row={row} />
            {canClear && (
              <Tooltip title="Stop storing this on the customer and inherit the plan's value">
                <IconButton
                  aria-label={`Inherit ${row.label} from the plan`}
                  onClick={() => onChange(row.name, REMOVE_OVERRIDE)}
                  size="small">
                  <SettingsBackupRestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* A switch leaves room beside it; a limit field and a select do not, and squeezing the
                caption in there clipped the value itself. */}
            {caption && row.kind === 'flag' && caption}
          </Stack>

          {caption && row.kind !== 'flag' && caption}
        </TableCell>

        <TableCell>
          <FeatureValueText muted name={row.name} value={row.planValue} />
        </TableCell>

        <TableCell>
          <CustomerFeatureUsage name={row.name} />
        </TableCell>

        <TableCell sx={{ maxWidth: 0, overflow: 'hidden' }}>
          <CustomerFeatureSource change={row.setBy} overridden={row.overridden} />
        </TableCell>

        <TableCell>
          <IconButton
            aria-expanded={open}
            aria-label={`History for ${row.label}`}
            onClick={() => setOpen(!open)}
            size="small">
            {open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ border: 0, p: 0 }}>
          <Collapse in={open} mountOnEnter timeout="auto" unmountOnExit>
            <CustomerFeatureHistory row={row} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default CustomerFeatureRow
